import { useEffect, useState } from 'react';
import { Draft, produce } from 'immer';
import { DEFAULT_NEWNESS_LOOKBACK_DAYS } from 'src/components/SetsConfigDetails/dateOverlap';
import { DEFAULT_NEWNESS_DISTRIBUTION } from 'src/components/SetsConfigDetails/newnessSet';

type ProductSetsConfig = Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;
type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;

interface UseEditableSetsConfigReturn {
    config: ProductSetsConfig;
    hasUnsavedChanges: boolean;
    setConfig: (config: ProductSetsConfig) => void;
    discardChanges: () => void;
    markAsSaved: () => void;
    updateProductName: (productName: string) => void;
    replaceCultureGroups: (groups: SetsConfigCultureGroup[]) => void;
    addGroup: () => void;
    removeGroup: (groupIndex: number) => void;
    updateGroupName: (groupIndex: number, name: string) => void;
    updateGroupCultures: (groupIndex: number, cultures: string[]) => void;
    updateGroupDates: (groupIndex: number, startDate: string | null, endDate: string | null) => void;
    toggleGroupNewness: (groupIndex: number, enabled: boolean) => void;
    updateGroupNewnessLookbackDays: (groupIndex: number, days: number | null) => void;
    updateGroupNewnessDistribution: (groupIndex: number, distribution: SetDistribution) => void;
    updateGroupNotes: (groupIndex: number, notes: string) => void;
    addSet: (groupIndex: number) => void;
    updateSet: (groupIndex: number, setIndex: number, update: Partial<SetDefinition>) => void;
    removeSet: (groupIndex: number, setIndex: number) => void;
}

const buildDefaultSet = (priority: number): SetDefinition => ({
    name: 'New set',
    priority,
    distribution: { type: 'probability', part: 0, offset: 0 },
    ranking: { type: 'semantic', keywordSearchPhrase: '' },
});

const nextSetPriority = (sets: SetDefinition[]): number => {
    if (sets.length === 0) {
        return 10;
    }

    return Math.max(...sets.map((set) => set.priority)) + 10;
};

export function useEditableSetsConfig(initialConfig: ProductSetsConfig): UseEditableSetsConfigReturn {
    const [config, setConfigState] = useState<ProductSetsConfig>(initialConfig);
    const [original, setOriginal] = useState<ProductSetsConfig>(initialConfig);

    const setConfig = (nextConfig: ProductSetsConfig): void => {
        setConfigState(nextConfig);
        setOriginal(nextConfig);
    };

    useEffect(() => {
        setConfig(initialConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialConfig]);

    const discardChanges = (): void => {
        setConfigState(original);
    };

    const markAsSaved = (): void => {
        setOriginal(config);
    };

    const updateProductName = (productName: string): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            // eslint-disable-next-line no-param-reassign
            draft.productName = productName;
        }));
    };

    // A structural copy of the incoming groups, so the pasted-in data has no shared references
    // back to the source config the user copied from.
    const replaceCultureGroups = (groups: SetsConfigCultureGroup[]): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            // eslint-disable-next-line no-param-reassign
            draft.cultureGroups = JSON.parse(JSON.stringify(groups));
        }));
    };

    const addGroup = (): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            draft.cultureGroups.push({
                name: '',
                cultures: [],
                startDate: null,
                endDate: null,
                newnessEnabled: true,
                newnessLookbackDays: DEFAULT_NEWNESS_LOOKBACK_DAYS,
                newnessDistribution: DEFAULT_NEWNESS_DISTRIBUTION,
                notes: '',
                sets: [],
            });
        }));
    };

    const removeGroup = (groupIndex: number): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            draft.cultureGroups.splice(groupIndex, 1);
        }));
    };

    const updateGroupName = (groupIndex: number, name: string): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.name = name;
            }
        }));
    };

    const updateGroupCultures = (groupIndex: number, cultures: string[]): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (!group) {
                return;
            }

            // Keep the auto-derived name synced to the culture list until the user renames the
            // group; once the name diverges from that derivation we treat it as intentional and
            // stop touching it on further culture changes.
            const nameIsAutoDerived = group.name === group.cultures.join(', ');

            group.cultures = cultures;

            if (nameIsAutoDerived) {
                group.name = cultures.join(', ');
            }
        }));
    };

    const updateGroupDates = (groupIndex: number, startDate: string | null, endDate: string | null): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.startDate = startDate;
                group.endDate = endDate;
            }
        }));
    };

    const toggleGroupNewness = (groupIndex: number, enabled: boolean): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.newnessEnabled = enabled;
            }
        }));
    };

    const updateGroupNewnessLookbackDays = (groupIndex: number, days: number | null): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.newnessLookbackDays = days;
            }
        }));
    };

    const updateGroupNewnessDistribution = (groupIndex: number, distribution: SetDistribution): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.newnessDistribution = distribution;
            }
        }));
    };

    const updateGroupNotes = (groupIndex: number, notes: string): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.notes = notes;
            }
        }));
    };

    const addSet = (groupIndex: number): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (!group) {
                return;
            }

            const priority = nextSetPriority(group.sets);

            group.sets.push(buildDefaultSet(priority));
        }));
    };

    const updateSet = (groupIndex: number, setIndex: number, update: Partial<SetDefinition>): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group && setIndex >= 0 && setIndex < group.sets.length) {
                // eslint-disable-next-line no-param-reassign
                group.sets[setIndex] = { ...group.sets[setIndex], ...update };
            }
        }));
    };

    const removeSet = (groupIndex: number, setIndex: number): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<ProductSetsConfig>) => {
            const group = draft.cultureGroups[groupIndex];

            if (group) {
                group.sets.splice(setIndex, 1);
            }
        }));
    };

    const hasUnsavedChanges = JSON.stringify(config) !== JSON.stringify(original);

    return {
        config,
        hasUnsavedChanges,
        setConfig,
        discardChanges,
        markAsSaved,
        updateProductName,
        replaceCultureGroups,
        addGroup,
        removeGroup,
        updateGroupName,
        updateGroupCultures,
        updateGroupDates,
        toggleGroupNewness,
        updateGroupNewnessLookbackDays,
        updateGroupNewnessDistribution,
        updateGroupNotes,
        addSet,
        updateSet,
        removeSet,
    };
}
